import type { TourActionContext, TourDefinition } from './types';
import { getRouter } from '@/router/instance';
import { useAuthStore } from '@/stores/auth';
import { clickAnimated, clickFirstRowIn, delay } from '@/utils/tour/actions';
import { waitForCondition } from '@/utils/tour/waitForElement';

/**
 * Scripted playback demo for Playbooks, told in creation order: first HOW a
 * playbook is made (the setup step and the section order), then the content
 * of an existing playbook section by section, then the runs that apply it.
 * The demo only navigates — entering the create wizard writes nothing (the
 * only write is its final "Create playbook" button, never clicked), and the
 * walk uses whatever playbooks and runs the org already has.
 */

// Whether the walk actually opened a playbook / a run — set synchronously
// by the open actions, so the scenes inside them can skip instantly on
// orgs with nothing to demo instead of burning their target timeouts one
// by one. The route checks cover resumes landing mid-tour.
let playbookOpened = false;
let runOpened = false;

// The whole row opens a playbook, and the list no longer shows a status
// column, so there is nothing to prefer an ACTIVE playbook by — take the
// first row the org has.
async function openFirstPlaybook(ctx: TourActionContext): Promise<void> {
  playbookOpened = false;
  playbookOpened = await clickFirstRowIn(ctx, 'pb.list-table');
}

const playbookOpen = (): boolean => {
  if (playbookOpened) {
    return true;
  }
  const uid = getRouter().currentRoute.value.params.uid;
  return typeof uid === 'string' && uid.length > 0;
};

const runOpen = (): boolean =>
  runOpened || getRouter().currentRoute.value.name === 'playbook-run-detail';

/**
 * A playbook without evaluations shows a perpetual "Generating playbook…"
 * alert on every section — nothing to demo there. Checked from the current
 * wizard page (the alert is playbook-wide, not per-section).
 */
const playbookHasEvaluations = (): boolean => !document.querySelector('.evaluations-step__alert');

/** The creation scenes only make sense for users who may create playbooks. */
const canCreatePlaybooks = (): boolean => useAuthStore().userPermissions.can_create_playbooks;

async function openFirstRun(ctx: TourActionContext): Promise<void> {
  runOpened = false;
  runOpened = await clickFirstRowIn(ctx, 'pb.runs-table');
}

/** Back through history — playbook pages live on :uid routes. */
async function goBackInHistory(): Promise<void> {
  getRouter().back();
  await delay(500);
}

/** Re-enter the (write-free) create wizard when stepping Back to it. */
async function reopenCreateWizard(): Promise<void> {
  if (!canCreatePlaybooks()) {
    return;
  }
  try {
    await getRouter().push({ name: 'playbook-create' });
  } catch {
    // Navigation declined — the scene's target wait decides.
  }
  await delay(400);
}

/**
 * Scenes self-locate into the opened playbook's pages: the routes are
 * parameterized, so each scene pushes its own section using the uid already
 * present in the current route. View mode only — no edit route.
 */
function ensureSection(routeName: string): () => Promise<void> {
  return async () => {
    const current = getRouter().currentRoute.value;
    if (current.name === routeName) {
      return;
    }
    const uid = current.params.uid;
    if (typeof uid !== 'string' || !uid) {
      return;
    }
    try {
      await getRouter().push({ name: routeName, params: { uid } });
    } catch {
      // Navigation declined — the scene's target wait decides.
    }
    await delay(400);
  };
}

/**
 * Expand the first finding in the run results accordion so its content
 * (answer, reasoning, evidence passages) is visible, and stamp it with a
 * runtime anchor so the NEXT scene spotlights JUST that finding — not the
 * whole results panel with the summary. If a finding is already open (a
 * replay: n-collapse headers toggle, folding it back up would be wrong),
 * the open one gets the stamp instead.
 */
async function expandFirstFinding(ctx: TourActionContext): Promise<void> {
  const results = document.querySelector<HTMLElement>('[data-tour="pb.run-results"]');
  if (!results || ctx.isCancelled()) {
    return;
  }
  for (const stale of document.querySelectorAll('[data-tour="pb.expanded-finding"]')) {
    stale.removeAttribute('data-tour');
  }
  const active = results.querySelector<HTMLElement>('.n-collapse-item--active');
  if (active) {
    active.dataset.tour = 'pb.expanded-finding';
    return;
  }
  // Naive puts the toggle handler on __header-main, not on __header —
  // synthetic clicks on the outer header never expand anything.
  const header = await waitForCondition(
    () => results.querySelector<HTMLElement>('.n-collapse-item .n-collapse-item__header-main'),
    3000
  );
  if (!header || ctx.isCancelled()) {
    return;
  }
  const item = header.closest<HTMLElement>('.n-collapse-item');
  header.scrollIntoView({ block: 'center', behavior: ctx.animate ? 'smooth' : 'auto' });
  ctx.setHighlight(item ?? header);
  if (ctx.animate) {
    await delay(600);
  }
  await clickAnimated(header, ctx);
  if (ctx.animate) {
    // Let the expanded content render before the next bubble anchors.
    await delay(700);
  }
  if (item) {
    item.dataset.tour = 'pb.expanded-finding';
  }
}

/**
 * Section items render collapsed to their title row. Ensure the section,
 * then open its first item so the scene shows actual content instead of a
 * stack of headers. Collapse state is session-only UI state, so this leaves
 * nothing persistent behind.
 */
function ensureSectionWithFirstItemOpen(routeName: string): () => Promise<void> {
  return async () => {
    await ensureSection(routeName)();
    const card = await waitForCondition(() => {
      const list = document.querySelector('[data-tour="pb.section-list"]');
      return list?.querySelector<HTMLElement>('.evaluation-card') ?? null;
    }, 3000);
    if (!card || !card.classList.contains('is-collapsed')) {
      return;
    }
    card.querySelector<HTMLElement>('.evaluation-card__collapse-toggle')?.click();
    // Let the expansion settle before the target is measured.
    await delay(300);
  };
}

export const playbooksTour: TourDefinition = {
  key: 'playbooks',
  title: 'Playbooks',
  description: 'How playbooks are built, what is inside, and their runs.',
  entryRoute: 'playbooks',
  requiredPermission: 'can_use_playbooks',
  onComplete: async () => {
    try {
      await getRouter().push({ name: 'playbooks' });
    } catch {
      // Navigation declined; completing the tour still stands.
    }
  },
  steps: [
    {
      id: 'list',
      route: 'playbooks',
      target: 'pb.list-table',
      placement: 'top',
      title: 'Your playbooks',
      body:
        'A playbook is a reusable set of AI checks for one contract type: ' +
        'clauses to validate, questions to answer, values to extract. ' +
        'First, let’s see how one is made.',
    },
    {
      id: 'create',
      route: 'playbooks',
      target: 'pb.create-button',
      placement: 'bottom-end',
      title: 'Creating a playbook',
      body: 'It starts here. I will open the setup for you.',
      prerequisite: canCreatePlaybooks,
      actions: [{ kind: 'click', target: 'pb.create-button' }],
    },
    {
      id: 'setup-details',
      target: 'pb.setup-details',
      placement: 'right',
      title: 'Playbook details',
      body:
        'Name the playbook and set what it covers: the contract type, the ' +
        'side you represent, and the language. Every check GAIA generates ' +
        'is steered by these.',
      prerequisite: canCreatePlaybooks,
      backActions: [{ kind: 'custom', run: goBackInHistory }],
    },
    {
      id: 'setup-reference',
      target: 'pb.setup-reference',
      placement: 'left',
      title: 'Creating the content',
      body:
        'Pick how the checks come to be: let GAIA generate them from a ' +
        'reference contract and your instructions, start from one of our ' +
        'templates, or begin with an empty playbook.',
      prerequisite: canCreatePlaybooks,
    },
    {
      id: 'setup-prompts',
      target: 'pb.setup-prompts',
      placement: 'top',
      title: 'Description and prompts',
      body:
        'Optional context that guides summaries and evaluations. You can ' +
        'leave it empty and refine it any time. Nothing is created until ' +
        'the final button, so we will leave this playbook unfinished.',
      prerequisite: canCreatePlaybooks,
    },
    {
      id: 'existing',
      route: 'playbooks',
      target: 'pb.list-table',
      placement: 'top',
      title: 'Inside a finished playbook',
      body:
        'To see what setup produces, let’s open a finished playbook and ' +
        'walk its sections one by one.',
      actions: [{ kind: 'custom', run: openFirstPlaybook }],
      backActions: [{ kind: 'custom', run: reopenCreateWizard }],
    },
    {
      id: 'extractions',
      prerequisite: () => playbookOpen() && playbookHasEvaluations(),
      onEnter: ensureSectionWithFirstItemOpen('playbook-extractions'),
      target: 'pb.section-list',
      placement: 'top',
      title: 'Extractions',
      body:
        'Typed values pulled from every document the playbook runs on: ' +
        'dates, amounts, parties, terms.',
    },
    {
      id: 'evaluations',
      prerequisite: () => playbookOpen() && playbookHasEvaluations(),
      onEnter: ensureSectionWithFirstItemOpen('playbook-evaluations'),
      target: 'pb.section-list',
      placement: 'top',
      title: 'Evaluations',
      body:
        'Multiple-choice checks with risk-leveled answers: the run picks ' +
        'the option the document supports.',
    },
    {
      id: 'uncommons',
      prerequisite: () => playbookOpen() && playbookHasEvaluations(),
      onEnter: ensureSectionWithFirstItemOpen('playbook-uncommons'),
      target: 'pb.section-list',
      placement: 'top',
      title: 'Uncommon terms',
      body:
        'Flags passages that are unusual for this contract type; finding ' +
        'thresholds can trigger actions automatically.',
    },
    {
      id: 'clauses',
      prerequisite: () => playbookOpen() && playbookHasEvaluations(),
      onEnter: ensureSectionWithFirstItemOpen('playbook-clauses'),
      target: 'pb.section-list',
      placement: 'top',
      title: 'Clause validation',
      body:
        'Each clause carries graded positions: preferred, fallback, and ' +
        'escalation, with follow-up actions attached to each.',
    },
    {
      id: 'runs',
      prerequisite: playbookOpen,
      onEnter: ensureSection('playbook-runs'),
      target: 'pb.runs-table',
      placement: 'top',
      title: 'Playbook runs',
      body:
        'Each run checks one document against the playbook, and results ' +
        'stream in as they finish. Let’s open one.',
      actions: [{ kind: 'custom', run: openFirstRun }],
    },
    {
      id: 'run-results',
      prerequisite: runOpen,
      target: 'pb.run-results',
      placement: 'right',
      title: 'What the run found',
      body:
        'A written summary up top, then every finding grouped by severity ' +
        'or type: what passed, and what needs your attention. Let me open ' +
        'the first finding.',
      backActions: [{ kind: 'custom', run: goBackInHistory }],
      actions: [{ kind: 'custom', run: expandFirstFinding }],
    },
    {
      id: 'run-evidence',
      prerequisite: runOpen,
      target: 'pb.expanded-finding',
      placement: 'right',
      title: 'Evidence inside the finding',
      body:
        'Each finding carries its full story: what was checked, the ' +
        'answer, and the evidence passages pulled from the document.',
    },
    {
      id: 'run-preview',
      prerequisite: runOpen,
      target: 'pb.run-preview',
      placement: 'left',
      title: 'The document alongside',
      body:
        'The reviewed document stays open on this side. Clicking a ' +
        'finding jumps the preview straight to the passage its evidence ' +
        'came from.',
    },
    {
      id: 'finale',
      title: 'That is Playbooks',
      body:
        'Define the checks once, run them on every document. Start a run ' +
        'by dropping a file on the runs page, or build your own playbook ' +
        'with Create playbook.',
    },
  ],
};
